import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { StripeSepaBillingAddress } from '@core/models/stripe-billing.model';
import { StripeService } from '@core/stripe/stripe.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, finalize, map, take } from 'rxjs/operators';
import { Stripe, StripeIbanElement } from '@stripe/stripe-js';

@UntilDestroy()
@Component({
  selector: 'wai-sepa-payment-setup',
  templateUrl: './sepa-payment-setup.component.html',
  styleUrls: ['./sepa-payment-setup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SepaPaymentSetupComponent implements AfterViewInit, OnDestroy {
  @Input() public billingName = '';
  @Input() public billingEmail = '';
  @Input() public processing = false;
  /** Optional pre-fill so non-EEA payers don't retype an address we already hold. */
  @Input() public billingAddress: Partial<StripeSepaBillingAddress> | null = null;
  /**
   * Trial length (days) of the tier being subscribed to, used to tell the payer
   * when the first debit will be taken (KAP-1855).
   * - `null`  → not a subscribe flow (e.g. updating a payment method); no date shown.
   * - `0`     → subscribe with no trial; first debit is initiated immediately.
   * - `> 0`   → subscribe with a trial; first debit falls at the end of the trial.
   */
  @Input() public trialPeriodDays: number | null = null;

  @Output() public completed = new EventEmitter<string>();
  @Output() public cancelled = new EventEmitter<void>();

  @ViewChild('ibanElementMount', { static: true }) public ibanElementMount!: ElementRef<HTMLDivElement>;

  public loading = false;
  public submitting = false;
  public ready = false;
  public isIbanComplete = false;
  public errorMessage: string | null = null;
  /** ISO-2 country of the entered IBAN, reported by the Stripe IBAN element. */
  public ibanCountry: string | null = null;
  public addressForm: FormGroup;

  private stripe: Stripe | null = null;
  private ibanElement: StripeIbanElement | null = null;
  private setupIntentClientSecret = '';

  /**
   * Real postal code formats by ISO 3166-1 alpha-2 country. The billing
   * address here has no country picker of its own (only the debtor's IBAN
   * country is known), but a payer's billing address doesn't necessarily
   * match their bank's country - e.g. a UK bank account with a Polish
   * billing address. So rather than only accepting the IBAN country's own
   * shape, a postal code is accepted if it matches ANY country in this list.
   */
  private static readonly POSTAL_CODE_FORMATS: Record<string, RegExp> = {
    GB: /^[A-Za-z]{1,2}[0-9][A-Za-z0-9]?\s?[0-9][A-Za-z]{2}$/, // SW1A 1AA
    CH: /^[0-9]{4}$/, // 8001
    MC: /^[0-9]{5}$/, // 98000
    SM: /^[0-9]{5}$/, // 47890
    AD: /^AD[0-9]{3}$/i, // AD500
    VA: /^[0-9]{5}$/, // 00120
    PL: /^\d{2}-\d{3}$/, // 00-001
    US: /^\d{5}(-\d{4})?$/, // 10001
    CA: /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/, // M5V 3L9
    DE: /^\d{5}$/, // 10115
    FR: /^\d{5}$/, // 75001
    IT: /^\d{5}$/, // 00118
    ES: /^\d{5}$/, // 28001
    PT: /^\d{4}-\d{3}$/, // 1000-001
    NL: /^\d{4}\s?[A-Za-z]{2}$/, // 1012 AB
    BE: /^\d{4}$/, // 1000
    AT: /^\d{4}$/, // 1010
    SE: /^\d{3}\s?\d{2}$/, // 111 20
    NO: /^\d{4}$/, // 0150
    DK: /^\d{4}$/, // 1050
    FI: /^\d{5}$/, // 00100
    IE: /^[A-Za-z]\d{2}\s?[A-Za-z0-9]{4}$/, // D02 X285 (Eircode)
    CZ: /^\d{3}\s?\d{2}$/, // 110 00
    SK: /^\d{3}\s?\d{2}$/, // 811 01
    HU: /^\d{4}$/, // 1051
    RO: /^\d{6}$/, // 010011
    BG: /^\d{4}$/, // 1000
    HR: /^\d{5}$/, // 10000
    SI: /^\d{4}$/, // 1000
    RS: /^\d{5,6}$/, // 11000
    GR: /^\d{3}\s?\d{2}$/, // 105 57
    TR: /^\d{5}$/, // 34000
    UA: /^\d{5}$/, // 01001
    LT: /^(LT-)?\d{5}$/i, // LT-01100
    LV: /^(LV-)?\d{4}$/i, // LV-1050
    EE: /^\d{5}$/, // 10111
    AU: /^\d{4}$/, // 2000
    NZ: /^\d{4}$/, // 1010
    JP: /^\d{3}-?\d{4}$/, // 100-0001
    KR: /^\d{5}$/, // 04524
    CN: /^\d{6}$/, // 100000
    IN: /^\d{6}$/, // 110001
    SG: /^\d{6}$/, // 018956
    MY: /^\d{5}$/, // 50000
    ID: /^\d{5}$/, // 10110
    TH: /^\d{5}$/, // 10100
    PH: /^\d{4}$/, // 1000
    VN: /^\d{6}$/, // 100000
    SA: /^\d{5}(-\d{4})?$/, // 12271
    IL: /^\d{5,7}$/, // 6100001
    ZA: /^\d{4}$/, // 8001
    EG: /^\d{5}$/, // 11511
    BR: /^\d{5}-?\d{3}$/, // 01001-000
    // AE (United Arab Emirates) intentionally omitted: postal codes aren't
    // generally used there, so any value falls through to rejection there,
    // matching that a code shouldn't be expected/entered at all.
  };

  /** Passes if the value matches ANY supported country's postal code shape. */
  private static postalCodeFormatValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      if (!control.value) {
        return null;
      }
      const value = control.value.toString().trim();
      const matchesAnyCountry = Object.values(SepaPaymentSetupComponent.POSTAL_CODE_FORMATS).some((pattern) =>
        pattern.test(value),
      );
      return matchesAnyCountry ? null : { postalCodeFormat: true };
    };
  }

  constructor(
    private stripeService: StripeService,
    private cdr: ChangeDetectorRef,
    private formBuilder: FormBuilder,
  ) {
    this.addressForm = this.formBuilder.group({
      line1: [''],
      line2: [''],
      postalCode: [''],
      city: [''],
    });
  }

  /**
   * True once a non-EEA IBAN is entered. Stripe rejects those without a full
   * address, so the fields appear only when they are actually needed rather
   * than adding friction for the EEA majority.
   */
  public get requiresBillingAddress(): boolean {
    return this.stripeService.requiresBillingAddress(this.ibanCountry);
  }

  /**
   * Whether to show any first-payment messaging. False outside a subscribe flow
   * (e.g. the update-payment-method dialog), where there is no "first payment".
   */
  public get showFirstPaymentInfo(): boolean {
    return this.trialPeriodDays !== null;
  }

  /** Trial-backed subscribe: the first debit is deferred to the trial's end. */
  public get hasTrial(): boolean {
    return (this.trialPeriodDays ?? 0) > 0;
  }

  /**
   * Estimated date the first SEPA debit will be initiated, for a trial subscribe.
   * `today + trialPeriodDays`. This is a client-side estimate shown before the
   * subscription exists; the authoritative date (Stripe's `trial_end`) is
   * confirmed after subscribe (KAP-1855). Null when there is no trial.
   */
  public get firstPaymentDate(): Date | null {
    if (!this.hasTrial) {
      return null;
    }
    const date = new Date();
    date.setDate(date.getDate() + (this.trialPeriodDays as number));
    return date;
  }

  public get canSubmit(): boolean {
    if (!this.ready || !this.isIbanComplete || this.loading || this.submitting || this.processing) {
      return false;
    }
    return !this.requiresBillingAddress || this.addressForm.valid;
  }

  public ngAfterViewInit(): void {
    this.initializeSepaSetup();
  }

  public ngOnDestroy(): void {
    if (this.ibanElement) {
      this.ibanElement.destroy();
      this.ibanElement = null;
    }
  }

  public onRetry(): void {
    this.initializeSepaSetup();
  }

  public onCancel(): void {
    this.cancelled.emit();
  }

  public onConfirm(): void {
    if (!this.stripe || !this.ibanElement || !this.setupIntentClientSecret || !this.isIbanComplete) {
      return;
    }

    if (this.requiresBillingAddress && this.addressForm.invalid) {
      // Surface which fields are missing rather than silently doing nothing —
      // a dead Confirm button was the original complaint on the add-on flow.
      this.addressForm.markAllAsTouched();
      this.cdr.markForCheck();
      return;
    }

    this.submitting = true;
    this.errorMessage = null;
    this.cdr.markForCheck();

    this.confirmMandateAndPersistPaymentMethod()
      .pipe(
        untilDestroyed(this),
        finalize(() => {
          this.submitting = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe((paymentMethodId) => {
        if (paymentMethodId) {
          this.completed.emit(paymentMethodId);
        }
      });
  }

  private initializeSepaSetup(): void {
    this.loading = true;
    this.ready = false;
    this.errorMessage = null;
    this.isIbanComplete = false;
    this.cdr.markForCheck();

    if (this.ibanElement) {
      this.ibanElement.destroy();
      this.ibanElement = null;
    }

    forkJoin({
      stripe: this.stripeService.getStripeInstance().pipe(take(1)),
      setupIntent: this.stripeService.createSepaSetupIntent().pipe(take(1)),
    })
      .pipe(
        untilDestroyed(this),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
        catchError((error) => {
          this.errorMessage = error?.error?.detail || error?.message || 'Failed to initialize SEPA payment setup.';
          return of(null);
        }),
      )
      .subscribe((result) => {
        if (!result || !result.stripe) {
          if (!this.errorMessage) {
            this.errorMessage = 'Stripe is currently unavailable.';
          }
          return;
        }

        this.stripe = result.stripe;
        this.setupIntentClientSecret = result.setupIntent.clientSecret;

        const elements = this.stripeService.createElements(this.stripe);
        this.ibanElement = this.stripeService.createIbanElement(elements);
        this.ibanElement.mount(this.ibanElementMount.nativeElement);

        this.ibanElement.on('change', (event) => {
          this.isIbanComplete = this.stripeService.isIbanComplete(event);
          this.errorMessage = event.error?.message || null;
          this.onIbanCountryChanged(event.country || null);
          this.cdr.markForCheck();
        });

        this.ready = true;
        this.cdr.markForCheck();
      });
  }

  /**
   * Apply or drop address validators as the debtor country changes. Validators
   * must be removed again when the user switches back to an EEA IBAN, otherwise
   * Confirm stays disabled on an address nobody needs to fill in.
   */
  private onIbanCountryChanged(country: string | null): void {
    const normalized = country ? country.trim().toUpperCase() : null;
    if (normalized === this.ibanCountry) {
      return;
    }
    this.ibanCountry = normalized;

    const required = this.requiresBillingAddress;
    const postalCodeValidators: ValidatorFn[] = [
      Validators.required,
      Validators.maxLength(10),
      SepaPaymentSetupComponent.postalCodeFormatValidator(),
    ];
    const fieldValidators: Record<string, ValidatorFn[]> = {
      line1: [Validators.required, Validators.minLength(3), Validators.maxLength(200)],
      line2: [Validators.maxLength(200)],
      postalCode: postalCodeValidators,
      city: [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(200),
        Validators.pattern(/^[A-Za-zÀ-ÖØ-öø-ÿ\s.'-]+$/),
      ],
    };
    for (const [field, validators] of Object.entries(fieldValidators)) {
      const control = this.addressForm.get(field);
      if (!control) {
        continue;
      }
      if (required) {
        control.setValidators(validators);
      } else {
        control.clearValidators();
      }
      control.updateValueAndValidity({ emitEvent: false });
    }

    if (required) {
      this.prefillAddress();
    }
  }

  private prefillAddress(): void {
    if (!this.billingAddress) {
      return;
    }
    // Only fill blanks — never overwrite what the user has already typed.
    const patch: Record<string, string> = {};
    const source: Record<string, string | undefined> = {
      line1: this.billingAddress.line1,
      line2: this.billingAddress.line2,
      postalCode: this.billingAddress.postalCode,
      city: this.billingAddress.city,
    };
    for (const [field, value] of Object.entries(source)) {
      if (value && !this.addressForm.get(field)?.value) {
        patch[field] = value;
      }
    }
    if (Object.keys(patch).length) {
      this.addressForm.patchValue(patch);
    }
  }

  private buildBillingAddress(): StripeSepaBillingAddress | null {
    if (!this.requiresBillingAddress || !this.ibanCountry) {
      return null;
    }
    const value = this.addressForm.value;
    return {
      line1: (value.line1 || '').trim(),
      line2: (value.line2 || '').trim() || undefined,
      postalCode: (value.postalCode || '').trim(),
      city: (value.city || '').trim(),
      // Derived from the IBAN rather than asked for. Stripe needs a country and
      // the bank's country is the best available answer; a payer banking abroad
      // is a known edge case, not handled here.
      country: this.ibanCountry,
    };
  }

  private confirmMandateAndPersistPaymentMethod(): Observable<string | null> {
    return new Observable<string | null>((subscriber) => {
      this.stripeService
        .confirmSepaMandate(
          this.stripe as Stripe,
          this.setupIntentClientSecret,
          this.ibanElement as StripeIbanElement,
          this.billingName || 'Kapnative User',
          this.billingEmail || undefined,
          this.buildBillingAddress(),
        )
        .then((confirmation) => {
          this.stripeService
            .recordSepaPaymentMethod(confirmation.paymentMethodId, confirmation.setupIntentId)
            .pipe(take(1), untilDestroyed(this))
            .subscribe({
              next: (recorded) => {
                subscriber.next(recorded.paymentMethodId);
                subscriber.complete();
              },
              error: (error) => {
                this.errorMessage = error?.error?.detail || error?.message || 'Failed to save payment method.';
                subscriber.next(null);
                subscriber.complete();
              },
            });
        })
        .catch((error) => {
          this.errorMessage = this.stripeService.normalizeStripeError(error);
          subscriber.next(null);
          subscriber.complete();
        });
    }).pipe(map((paymentMethodId) => paymentMethodId || null));
  }
}
